import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Button, SkipLink } from "../components/ui";
import { LMS_VERSION, vtag, type RuntimeConfig } from "../config";
import { RouteFocus } from "./RouteFocus";

/**
 * The shell (design.md §10): skip link first, then header, one main
 * navigation, <main id="main"> that the skip link and RouteFocus can land
 * on, and a footer with the build's version beside a link to its release
 * notes (release-notes template). Role-based areas render inside from the
 * permission matrix once it exists; for now, the public shell.
 */
export function AppLayout({ config }: { config: RuntimeConfig }) {
  const { status, me, signOut } = useAuth();
  const version = vtag(LMS_VERSION);
  const apiVersion = me?.version;
  const nonProd = config.envName !== "prod";
  // The tenant's own product name once /v1/me has answered (TEN-04); the build-time default before.
  const productName = me?.tenant.productName ?? config.productName;
  const links = [
    { to: "/", label: "Home", end: true },
    ...(status === "signed-in" ? [{ to: "/account/security", label: "Security", end: false }] : []),
    // Areas render from the matrix's permissions, never from a role name (permission-matrix.md §6).
    ...(me?.permissions.includes("audit_event.list") ? [{ to: "/program/audit", label: "Audit trail", end: false }] : []),
    { to: "/docs/release-notes", label: "Release notes", end: false },
    ...(nonProd ? [{ to: "/primitives", label: "Primitives", end: false }] : []),
  ];
  return (
    <div className="flex min-h-dvh flex-col">
      <SkipLink />
      <RouteFocus productName={productName} />
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <NavLink to="/" className="inline-flex min-h-11 items-center text-lg font-semibold text-ink no-underline">
            {productName}
          </NavLink>
          <nav aria-label="Main">
            <ul className="flex flex-wrap items-center gap-1">
              {links.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) =>
                      `inline-flex min-h-11 items-center rounded-lg px-3 no-underline ${isActive ? "bg-canvas font-semibold text-ink" : "text-ink hover:bg-canvas"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
              {status === "signed-in" && me ? (
                <li className="ml-2 flex items-center gap-2">
                  <span className="text-sm text-muted" data-testid="signed-in-as">
                    {me.person.displayName}
                  </span>
                  <Button onClick={() => void signOut()}>Sign out</Button>
                </li>
              ) : (
                status === "signed-out" && (
                  <li>
                    <NavLink to="/login" className="inline-flex min-h-11 items-center rounded-lg px-3 text-ink no-underline hover:bg-canvas">
                      Sign in
                    </NavLink>
                  </li>
                )
              )}
            </ul>
          </nav>
        </div>
      </header>
      <main id="main" tabIndex={-1} className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Outlet />
      </main>
      <footer className="border-t border-line bg-surface">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 text-sm text-muted">
          <p>
            {productName} <span data-testid="version">{version}</span>
            {apiVersion && vtag(apiVersion) !== version && (
              // A stale cached bundle is visible instead of mysterious (release-notes template).
              <span data-testid="api-version"> (API {vtag(apiVersion)})</span>
            )}
            {nonProd && (
              <>
                {" · "}
                <span data-testid="environment">{config.envName}</span>
              </>
            )}
          </p>
          <NavLink to={`/docs/release-notes#${version}`} className="inline-flex min-h-11 items-center">
            What changed in {version}
          </NavLink>
        </div>
      </footer>
    </div>
  );
}
